import React, { useEffect, useRef } from 'react';
import { WebPlayerView } from 'unified-video-framework/web';

/**
 * AdminMiniPlayer - A reusable video player component with minimal controls
 *
 * @param {string} url - Video URL
 * @param {string} videoFormat - Video format (e.g., 'application/x-mpegurl', 'video/mp4')
 * @param {string} title - Video title
 * @param {string} poster - Poster image URL
 * @param {string} className - Additional CSS class
 * @param {object} watermark - Watermark configuration
 * @param {object} settings - Settings configuration
 * @param {number} volume - Initial volume (0-1)
 * @param {boolean} autoPlay - Auto play on load
 * @param {boolean} muted - Muted on load
 */
const AdminMiniPlayer = ({
  url,
  videoFormat,
  title,
  poster,
  className = 'playerHeight',
  watermark = null,
  settings = { subtitles: false, speed: false, quality: true },
  volume = 0.8,
  autoPlay = false,
  muted = false,
}) => {
  // Use unique ID to identify this specific player instance
  const playerId = useRef(`player-${Math.random().toString(36).substr(2, 9)}`);
  const playerInstance = useRef(null);

  // Auto-detect video type from format or URL
  const getVideoType = () => {
    const f = (videoFormat || '').toLowerCase();
    const u = (url || '').toLowerCase();

    if (f.includes('mpegurl') || u.endsWith('.m3u8') || f.includes('hls')) return 'hls';
    if (f.includes('dash') || u.endsWith('.mpd')) return 'dash';
    if (f.includes('mp4') || u.endsWith('.mp4')) return 'mp4';
    if (f.includes('webm') || u.endsWith('.webm')) return 'webm';

    return 'auto';
  };

  // Sync state between multiple players: pause others when this one starts
  useEffect(() => {
    const handleGlobalPlay = (e) => {
      if (e.detail?.playerId !== playerId.current && playerInstance.current) {
        try {
          // If the framework's player instance has a pause method
          if (typeof playerInstance.current.pause === 'function') {
            playerInstance.current.pause();
          } else if (playerInstance.current.el && typeof playerInstance.current.el().pause === 'function') {
            // Fallback for video.js style wrappers
            playerInstance.current.el().pause();
          }
        } catch (err) {
          console.warn('[AdminMiniPlayer] Failed to pause player instance:', err);
        }
      }
    };

    window.addEventListener('nexus-player-play', handleGlobalPlay);
    return () => window.removeEventListener('nexus-player-play', handleGlobalPlay);
  }, []);

  const handleReady = (player) => {
    playerInstance.current = player;
  };

  const handlePlay = () => {
    // Notify all other players that this one has started playing
    const playEvent = new CustomEvent('nexus-player-play', {
      detail: { playerId: playerId.current }
    });
    window.dispatchEvent(playEvent);
  };

  return (
    <div className="video-player-wrapper" style={{ position: 'relative', width: '100%', maxWidth: '100%', minHeight: '300px' }}>
      <style>{`
                .video-player-wrapper .uvf-player-container {
                    position: relative !important;
                    top: 0 !important;
                    left: 0 !important;
                    right: 0 !important;
                    bottom: 0 !important;
                    min-height: 300px !important;
                }
                .video-player-wrapper .playerHeight,
                .video-player-wrapper .adminVideoPlayer {
                    min-height: 300px !important;
                }
            `}</style>
      <WebPlayerView
        url={url}
        type={getVideoType()}
        controls={true}
        controlsVisibility={{
          playback: {
            centerPlayButton: false,
            skipButtons: false,
          },
          features: {
            shareButton: false,
            castButton: false,
          },
          display: {
            fullscreenButton: true,
            pipButton: false,
          },
          chrome: {
            navigationButtons: false,
            frameworkBranding: false,
          }
        }}
        metadata={{
          title: title,
          poster: poster,
        }}
        watermark={watermark}
        settings={settings}
        autoPlay={autoPlay}
        muted={muted}
        volume={volume}
        playsInline={true}
        className={className}
        onReady={handleReady}
        onPlay={handlePlay}
      />
    </div>
  );
};

export default AdminMiniPlayer;